"use client";

import React, { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import API from "@/utils/api";
import { toast } from "react-toastify";
import Breadcrumbs from "@/components/Breadcrumbs.jsx";
import { useAuth } from "@/context/AuthContext";

const ALL_CATEGORIES = [
  { value: "faq", label: "FAQ" },
  { value: "support", label: "Support" },
  { value: "announcements", label: "Announcements" },
  { value: "fun", label: "Fun" },
  { value: "e-books", label: "E-BOOKS" },
];


const ARTICLE_CATEGORIES = ALL_CATEGORIES.filter((c) =>
  ["faq", "announcements"].includes(c.value)
);

export default function NewThreadPage() {
  const router = useRouter();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [form, setForm] = useState({
    title: "",
    body: "",
    category: "faq",
    tags: "",
    type: "discussion", // discussion | article
    summary: "",
    pinned: false,
    locked: false,
  });

  const [submitting, setSubmitting] = useState(false);

  const LABEL_BY_VALUE = useMemo(
    () => Object.fromEntries(ALL_CATEGORIES.map((c) => [c.value, c.label])),
    []
  );

  const categoryOptions = useMemo(
    () => (form.type === "article" ? ARTICLE_CATEGORIES : ALL_CATEGORIES),
    [form.type]
  );

  const crumbs = useMemo(() => {
    const items = [{ label: "Community", href: "/community" }];
    if (form.category) {
      const label = LABEL_BY_VALUE[form.category] || form.category;
      items.push({ label, href: `/community/category/${form.category}` });
    }
    items.push({ label: "New Thread" });
    return items;
  }, [form.category, LABEL_BY_VALUE]);

  const handleTypeChange = (nextType) => {
    setForm((f) => {
      const next = { ...f, type: nextType };

      if (nextType === "article") {
        if (!["faq", "announcements"].includes(next.category)) next.category = "faq";
        if (!isAdmin) next.locked = true;
        if (isAdmin && typeof f.locked === "undefined") next.locked = true;
      }

      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);

      const tags = form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      const payload = {
        title: form.title,
        body: form.body,
        category: form.category,
        type: form.type,
        ...(form.summary ? { summary: form.summary.slice(0, 280) } : {}),
        tags,
      };

      if (isAdmin) {
        payload.pinned = !!form.pinned;
        payload.locked =
          form.type === "article"
            ? (typeof form.locked === "boolean" ? form.locked : true)
            : !!form.locked;
      }

      const res = await API.post("/community/thread", payload);

      toast.success("Thread created!");

      const { _id, slug, canonicalUrl } = res.data || {};
      const url =
        canonicalUrl ||
        (_id && slug ? `/community/thread/${_id}/${slug}` : `/community/thread/${_id}`);

      router.push(url);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to create thread");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-background text-textPrimary min-h-screen">
      <Breadcrumbs items={crumbs} className="mb-3" />
      <h1 className="text-2xl font-bold mb-6 text-primary">Start a New Thread</h1>

      <form onSubmit={handleSubmit} className="space-y-5">
        <input
          type="text"
          dir="auto"
          placeholder="Thread title"
          className="w-full border border-border bg-surface text-textPrimary placeholder:text-textSecondary p-3 rounded"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
        />

        <textarea
          dir="auto"
          placeholder="Write your post here..."
          className="w-full border border-border bg-surface text-textPrimary placeholder:text-textSecondary p-3 rounded"
          rows="6"
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          required
        />

        <div className="flex items-center gap-3">
          <label className="text-sm text-textSecondary">Type:</label>
          <select
            className="border border-border bg-surface text-textPrimary p-2 rounded"
            value={form.type}
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            <option value="discussion">Discussion</option>
            <option value="article">Article (FAQ / Announcements)</option>
          </select>
        </div>

        {form.type === "article" && (
          <div>
            <textarea
              dir="auto"
              placeholder="Short summary (optional, shown on cards) — 280 chars max"
              className="w-full border border-border bg-surface text-textPrimary placeholder:text-textSecondary p-3 rounded"
              rows="3"
              maxLength={280}
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
            />
            <p className="text-xs text-textSecondary mt-1">
              {280 - (form.summary?.length || 0)} characters left
            </p>
          </div>
        )}

        <select
          className="w-full border border-border bg-surface text-textPrimary p-3 rounded"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
        >
          {categoryOptions.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Tags (comma-separated)"
          className="w-full border border-border bg-surface text-textPrimary placeholder:text-textSecondary p-3 rounded"
          value={form.tags}
          onChange={(e) => setForm({ ...form, tags: e.target.value })}
        />

        {isAdmin && (
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.pinned}
                onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
              />
              Pinned
            </label>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={
                  form.type === "article"
                    ? (typeof form.locked === "boolean" ? form.locked : true)
                    : !!form.locked
                }
                onChange={(e) => setForm({ ...form, locked: e.target.checked })}
              />
              Locked
            </label>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="bg-primary text-white px-5 py-2 rounded hover:bg-accent transition disabled:opacity-60"
        >
          {submitting ? "Creating…" : "Create Thread"}
        </button>
      </form>
    </div>
  );
}
